<!--
  EntityView.vue — the generic entity CRUD screen (route /entity/:type/:id?).
  Purpose: drive the config-driven entity engine (Pattern B). It registers the
  `entity-form` Vuex module (installEntityForm), builds the Employee descriptor
  (the concrete kind the engine ships), and dispatches `entity-form/setup({
  entityConfig, id })` — which runs the descriptor's client-side layout synthesis,
  builds a record Model from the 19-type field registry, and (with an :id) fetches
  the record. Fields render through the engine's `entity-input-text` widget; Save
  dispatches `entity-form/maybeSave(values)` → validate → Model.save() → applyAvatar
  → callbacks. Only the `employee` kind is wired here; the full type2entities
  registry lives in the entity engine (ENTITY-BUILDER §3).
  Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
  (module `entity-form`, actions setup/maybeSave, itemCfg employee/* routes,
  entity-input-<type> dispatch — ENTITY-BUILDER §2/§3/§8), internal impl re-authored.
-->
<template>
  <v-container fluid>
    <v-toolbar flat dense color="transparent">
      <v-toolbar-title>{{ typeLabel }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-chip small v-if="ready && !unsupported" :color="isNew ? 'blue' : 'green'" text-color="white">
        {{ isNew ? 'create' : 'edit' }}
      </v-chip>
    </v-toolbar>
    <v-divider></v-divider>

    <!-- engine missing -->
    <v-alert v-if="engineError" :value="true" type="info" outline>
      Entity engine (src/entity) is not wired — expected
      <span class="tracko-mono">entity-form.store.js</span> +
      <span class="tracko-mono">configs/employee.js</span>. See BUILD.md.
    </v-alert>

    <!-- kind not wired in this demo shell -->
    <v-alert v-else-if="unsupported" :value="true" type="info" outline>
      Only the <span class="tracko-mono">employee</span> kind is wired in this shell.
      The full <span class="tracko-mono">type2entities</span> registry (place, department,
      vehicle, garage, driver, …) lives in the entity engine.
    </v-alert>

    <template v-else>
      <div v-if="loading || !ready" class="tracko-center pa-5">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <v-card v-else class="pa-3">
        <v-form>
          <component
            v-for="f in fields"
            :is="componentFor(f)"
            :key="f.id"
            :field="f"
            :value="form[f.id]"
            :label="f.label"
            :disabled="!!f.readonly"
            @input="onField(f.id, $event)"
          ></component>
        </v-form>

        <v-alert :value="!!saveError" type="error" outline dismissible @input="saveError = ''">
          {{ saveError }}
        </v-alert>
        <v-alert :value="saved" type="success" outline>Saved.</v-alert>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :loading="saving" @click="save">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import { installEntityForm } from '@/entity/entity-form.store.js'
import { createEmployeeEntity } from '@/entity/configs/employee.js'
import EntityInputText from '@/entity/inputs/EntityInputText.vue'
import EntityInputSelect from '@/entity/inputs/EntityInputSelect.vue'
import EntityInputDate from '@/entity/inputs/EntityInputDate.vue'
import EntityInputLocation from '@/entity/inputs/EntityInputLocation.vue'
import EntityInputTags from '@/entity/inputs/EntityInputTags.vue'

// field.type → input component. Types without a bespoke widget reuse the text input
// (the deployed getFormCmp fallback). Mirrors EntityForm.vue's TYPE_TO_COMPONENT so every
// field type is editable — not just text (select/date/location/tags previously rendered disabled).
const TYPE_TO_COMPONENT = {
  text: 'entity-input-text', bigtext: 'entity-input-text', email: 'entity-input-text',
  phone: 'entity-input-text', integer: 'entity-input-text', decimal: 'entity-input-text',
  password: 'entity-input-text', tracker: 'entity-input-select',
  select: 'entity-input-select', employee: 'entity-input-select',
  date: 'entity-input-date', datetime: 'entity-input-date',
  location: 'entity-input-location', tags: 'entity-input-tags', multi_employee: 'entity-input-tags'
}

export default {
  name: 'EntityView',
  components: {
    'entity-input-text': EntityInputText,
    'entity-input-select': EntityInputSelect,
    'entity-input-date': EntityInputDate,
    'entity-input-location': EntityInputLocation,
    'entity-input-tags': EntityInputTags
  },
  data() {
    return {
      engineError: false,
      unsupported: false,
      ready: false,
      form: {},
      saving: false,
      saved: false,
      saveError: ''
    }
  },
  computed: {
    typeLabel() {
      const type = this.$route.params.type
      const key = 'entities.type2labels.' + type
      const label = this.$t(key)
      return label && label !== key ? label : this.$t('entities.title')
    },
    fields() {
      return this.getter('entity-form/fields') || []
    },
    loading() {
      return !!this.getter('entity-form/loading')
    },
    isNew() {
      const g = this.getter('entity-form/isNew')
      return g == null ? true : g
    }
  },
  watch: {
    '$route.params.type'() {
      this.setup()
    },
    '$route.params.id'() {
      this.setup()
    }
  },
  created() {
    try {
      installEntityForm(this.$store) // registers the `entity-form` namespaced module
    } catch (err) {
      this.engineError = true
      // eslint-disable-next-line no-console
      console.warn('[tracko] entity engine not available:', err && err.message)
      return
    }
    this.setup()
  },
  beforeDestroy() {
    try {
      this.$store.dispatch('entity-form/reset')
    } catch (e) {
      /* module may already be gone */
    }
  },
  methods: {
    getter(key) {
      const g = this.$store && this.$store.getters
      return g && Object.prototype.hasOwnProperty.call(g, key) ? g[key] : undefined
    },
    async setup() {
      if (this.engineError) return
      this.ready = false
      this.saved = false
      this.saveError = ''
      const type = this.$route.params.type
      if (type !== 'employee') {
        this.unsupported = true
        return
      }
      this.unsupported = false
      try {
        await this.$store.dispatch('entity-form/setup', {
          entityConfig: createEmployeeEntity(),
          id: this.$route.params.id
        })
      } catch (err) {
        // Fetch may fail without a backend/session; still show a create form.
        // eslint-disable-next-line no-console
        console.warn('[tracko] entity-form setup (fetch?) failed:', err && err.message)
      }
      this.form = Object.assign({}, this.getter('entity-form/values') || {})
      this.ready = true
    },
    onField(id, value) {
      this.$set(this.form, id, value)
    },
    // Resolve a field's editor component from its type (falls back to the text input).
    componentFor(f) {
      return (f && f.input) || TYPE_TO_COMPONENT[f && f.type] || 'entity-input-text'
    },
    async save() {
      this.saving = true
      this.saved = false
      this.saveError = ''
      try {
        const res = await this.$store.dispatch('entity-form/maybeSave', this.form)
        if (res === false) {
          const errors = this.getter('entity-form/errors') || {}
          const first = Object.keys(errors)[0]
          this.saveError = first ? errors[first] : 'Please fix the highlighted fields.'
        } else {
          this.saved = true
        }
      } catch (err) {
        this.saveError =
          err && /unknown action type/.test(String(err.message || ''))
            ? 'Entity engine action not available.'
            : 'Save failed (backend/session?).'
        // eslint-disable-next-line no-console
        console.warn('[tracko] entity save failed:', err && err.message)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
